import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { SxProps } from '@mui/material'
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers'
import { Dayjs } from 'dayjs'
import { StyledMonthPicker } from '../../styles/MonthPickerStyles'
import StyledArrowIconButton from './StyledArrowIconButton'

export interface DatePickerWithArrowsProps extends Omit<DatePickerProps<Dayjs>, 'value' | 'onChange'> {
  value: Dayjs | null
  onChange: (date: Dayjs | null) => void
  view?: 'day' | 'month' | 'year'
  containerSxProps?: SxProps
}

const DatePickerWithArrows = ({
  value,
  onChange,
  view = 'day',
  containerSxProps,
  ...datePickerProps
}: DatePickerWithArrowsProps) => {
  const { minDate, maxDate, disabled } = datePickerProps

  const isArrowDisabled = (date: Dayjs | null | undefined) => {
    return disabled || !value || (date ? value.isSame(date, view) : false)
  }

  const handleDateChange = (direction: 'previous' | 'next') => {
    if (!value || disabled) return

    const offset = direction === 'previous' ? -1 : 1
    onChange(value.add(offset, view))
  }

  const handlePrevious = () => handleDateChange('previous')

  const handleNext = () => handleDateChange('next')

  const getAriaLabel = (direction: 'previous' | 'next') => {
    const action = direction === 'previous' ? 'Previous' : 'Next'
    return `${action} ${view}`
  }

  return (
    <StyledMonthPicker sx={containerSxProps}>
      <StyledArrowIconButton
        aria-label={getAriaLabel('previous')}
        size="small"
        onClick={handlePrevious}
        disabled={isArrowDisabled(minDate)}
      >
        <ChevronLeft />
      </StyledArrowIconButton>

      <DatePicker value={value} onChange={onChange} {...datePickerProps} />

      <StyledArrowIconButton
        aria-label={getAriaLabel('next')}
        size="small"
        onClick={handleNext}
        disabled={isArrowDisabled(maxDate)}
      >
        <ChevronRight />
      </StyledArrowIconButton>
    </StyledMonthPicker>
  )
}

export default DatePickerWithArrows
